import {
  ScanVaultPage,
  VulnerabilityDashboardResponse,
  VulnerabilityResourceTrend,
  ScanVaultCVE,
  ScanVaultPackage,
  ScanVaultFinding,
  VulnerabilityResourceInsightResponse,
} from './client';
import { scanVaultCvssV3 } from './cvss';

const severities = ['critical', 'high', 'medium', 'low', 'unknown'] as const;

const emptySeverityCounts = () => ({
  critical: 0,
  high: 0,
  medium: 0,
  low: 0,
  unknown: 0,
});

const addTrend = (
  target: Record<string, Record<string, number>>,
  day: string,
  severity: string,
  value: number,
) => {
  target[day] ??= emptySeverityCounts();
  const key = severity.toLowerCase();
  target[day][key] = (target[day][key] ?? 0) + value;
};

const mapResourceTrends = (trends: VulnerabilityResourceTrend[] = []) => {
  const bySeverity: Record<string, Record<string, number>> = {};
  const byType: Record<string, Record<string, number>> = {};
  const resolvedBySeverity: Record<string, Record<string, number>> = {};
  const resolvedByType: Record<string, Record<string, number>> = {};
  const resourcesBySeverity: Record<string, Record<string, number>> = {};
  const resolvedResourcesBySeverity: Record<
    string,
    Record<string, number>
  > = {};

  const addResourceTrend = (
    target: Record<string, Record<string, number>>,
    severity: string,
    day: string,
    value: number,
  ) => {
    const key = `${severity.toLowerCase()}_resources`;
    target[key] ??= {};
    target[key][day] = (target[key][day] ?? 0) + value;
  };

  trends.forEach(trend => {
    const day = trend.day.slice(0, 10);
    const severity = trend.severity.toLowerCase();
    const openResourceCount =
      trend.opened_resource_count ?? trend.vulnerable_resource_count;
    addTrend(bySeverity, day, severity, openResourceCount);
    addTrend(resolvedBySeverity, day, severity, trend.closed_resource_count);
    addResourceTrend(resourcesBySeverity, severity, day, openResourceCount);
    addResourceTrend(
      resolvedResourcesBySeverity,
      severity,
      day,
      trend.closed_resource_count,
    );

    byType[day] ??= {};
    resolvedByType[day] ??= {};
    byType[day][trend.resource_type] =
      (byType[day][trend.resource_type] ?? 0) +
      (trend.opened_resource_count ?? trend.vulnerable_resource_count);
    resolvedByType[day][trend.resource_type] =
      (resolvedByType[day][trend.resource_type] ?? 0) +
      trend.closed_resource_count;
  });

  const toLegacy = (source: Record<string, Record<string, number>>) =>
    Object.fromEntries(
      Object.entries(source).map(([day, values]) => [
        day,
        {
          Critical: values.critical ?? 0,
          High: values.high ?? 0,
          Medium: values.medium ?? 0,
          Low: values.low ?? 0,
          Unknown: values.unknown ?? 0,
        },
      ]),
    );

  const toTypeLegacy = (source: Record<string, Record<string, number>>) =>
    Object.entries(source).reduce(
      (result, [day, values]) => {
        result.unpatched_os[day] = Object.entries(values)
          .filter(([type]) => /os|virtualmachine|ec2/i.test(type))
          .reduce((sum, [, count]) => sum + count, 0);
        result.unpatched_technology[day] = Object.entries(values)
          .filter(([type]) => !/os|virtualmachine|ec2/i.test(type))
          .reduce((sum, [, count]) => sum + count, 0);
        return result;
      },
      {
        unpatched_os: {} as Record<string, number>,
        unpatched_technology: {} as Record<string, number>,
      },
    );

  return {
    bySeverity: toLegacy(bySeverity),
    resolvedBySeverity: toLegacy(resolvedBySeverity),
    byType: toTypeLegacy(byType),
    resolvedByType: toTypeLegacy(resolvedByType),
    resourcesBySeverity,
    resolvedResourcesBySeverity,
  };
};

export function mapDashboardToLegacy(response: VulnerabilityDashboardResponse) {
  const severity = emptySeverityCounts();
  response.severity.forEach(item => {
    if (item.key in severity) severity[item.key] = item.finding_count;
  });

  const sourceItems = (response.source_severity ?? response.sources ?? []).map(
    item => ({
      agnostic_name: item.key,
      vulnerability_count: {
        ...emptySeverityCounts(),
        ...((item as any).severity_counts ?? {}),
      },
    }),
  );

  const resourceTrends = mapResourceTrends(response.resource_trends);
  const prioritization = response.prioritization ?? {
    vulnerable_packages: 0,
    internet_exposed_resource_packages: 0,
    internet_exposed_resource_packages_with_exploitable_cve: 0,
    internet_exposed_resource_patchable_packages_with_exploitable_cve: 0,
    internet_exposed_resources_having_patchable_packages_with_exploitable_cve: 0,
  };

  return {
    vulnerabilitiesBySeverityCounts: severity,
    vulnerabilitiesBySources: { items: sourceItems, clouds: [] },
    openVulnerabilitiesBySeverity: resourceTrends.bySeverity,
    resolvedVulnerabilitiesBySeverity: resourceTrends.resolvedBySeverity,
    hostsWithOpenVulnerabilities: resourceTrends.resourcesBySeverity,
    hostsWithResolvedVulnerabilities:
      resourceTrends.resolvedResourcesBySeverity,
    hostsWithOpenVulnerabilitiesByType: resourceTrends.byType,
    hostsWithResolvedVulnerabilitiesByType: resourceTrends.resolvedByType,
    vulnerabilityPrioritization: {
      vulnerable_packages: prioritization.vulnerable_packages,
      internet_exposed_resource_packages:
        prioritization.internet_exposed_resource_packages,
      internet_exposed_resource_packages_with_exploitable_cve:
        prioritization.internet_exposed_resource_packages_with_exploitable_cve,
      internet_exposed_resource_patchable_packages_with_exploitable_cve:
        prioritization.internet_exposed_resource_patchable_packages_with_exploitable_cve,
      internet_exposed_resources_having_patchable_packages_with_explitable_cve:
        prioritization.internet_exposed_resources_having_patchable_packages_with_exploitable_cve,
      host_types: prioritization.host_types ?? [],
      clouds: [],
    },
    vulnerableImages: {
      items: (response.vulnerable_images ?? []).map(item => ({
        image: item.image,
        image_count: item.image_count,
        resource_count: item.resource_count,
        severity_counts: item.severity_counts ?? {},
      })),
    },
    patchablePackages: response.patchable_packages.map(item => ({
      package_name: item.name,
      installed_version: item.version,
      package_category_name: item.ecosystem,
      cve_ids: item.cve_ids,
      vulnerability: {
        ...emptySeverityCounts(),
        ...(item.severity_counts ?? {
          [item.severity.toLowerCase()]: 1,
        }),
      },
      patch_versions: item.fixed_versions ?? [],
      entities: {
        ...(item.provider_counts ?? {}),
        total: item.affected_resource_count,
      },
      is_patchable: item.patchable,
      last_seen: item.last_seen_at,
    })),
  };
}

export function mapCVEsToLegacy(page: ScanVaultPage<ScanVaultCVE>) {
  return {
    items: page.items.map(item => ({
      cve_id: item.cve_id,
      vulnerability_id: item.cve_id,
      title: item.title ?? item.cve_id,
      description: item.description,
      remediation: item.remediation,
      severity: item.severity,
      cvss_score: item.cvss_score,
      cvss_v3_score: item.cvss_score,
      cvss_v3_vector: scanVaultCvssV3(item).vector,
      cvss_v3: scanVaultCvssV3(item),
      fixed_in_version: item.fixed_version,
      patch_versions: item.fixed_version ? [item.fixed_version] : [],
      entities: { total: item.affected_resource_count },
      affected_resource_count: item.affected_resource_count,
      known_exploited: item.known_exploited,
      exploitable: item.exploitable,
      patchable: item.patchable,
      is_patchable: item.patchable,
      last_seen: item.last_seen_at,
      last_seen_at: item.last_seen_at,
      first_seen_at: item.first_seen_at,
      package_name: item.package_name,
      installed_version: item.package_version,
      published_date: item.published_date,
      last_modified_date: item.last_modified_date,
      data_source: item.data_source,
      references: item.references ?? [],
      cwe_ids: item.cwe_ids ?? [],
    })),
    total: page.total_count ?? page.items.length,
  };
}

export function mapPackagesToLegacy(page: ScanVaultPage<ScanVaultPackage>) {
  return {
    items: page.items.map(item => ({
      package_name: item.name,
      installed_version: item.version,
      package_category_name: item.ecosystem,
      package_category: item.ecosystem,
      vulnerability: {
        ...emptySeverityCounts(),
        ...(item.severity_counts ?? {
          [item.severity.toLowerCase()]: 1,
        }),
      },
      cve_ids: item.cve_ids,
      patch_versions: item.fixed_versions ?? [],
      entities: {
        ...(item.provider_counts ?? {}),
        total: item.affected_resource_count,
      },
      is_patchable: item.patchable,
      last_seen: item.last_seen_at,
    })),
    total: page.total_count ?? page.items.length,
  };
}

export function mapFindingsToLegacy(page: ScanVaultPage<ScanVaultFinding>) {
  return {
    items: page.items,
    total: page.total_count ?? page.items.length,
  };
}

export function mapVulnerabilityResourceInsightToLegacy(
  response: VulnerabilityResourceInsightResponse,
  page = 1,
  size = response.items.length || 40,
) {
  return {
    items: response.items.map(item => {
      const attrs = item.attrs ?? {};
      const deployedIn = attrs.deployed_in ?? attrs.deployedIn;
      let normalizedDeployedIn: Record<string, any> | undefined;
      if (deployedIn && typeof deployedIn === 'object') {
        normalizedDeployedIn = deployedIn as Record<string, any>;
      } else if (typeof deployedIn === 'string') {
        try {
          const parsed = JSON.parse(deployedIn);
          if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            normalizedDeployedIn = parsed;
          }
        } catch {
          // Some resource types expose placement as a scalar; the legacy
          // container table only renders the structured provider IDs/names.
        }
      }
      return {
        uuid: item.resource_uuid,
        entity_uuid: item.resource_uuid,
        resource_uuid: item.resource_uuid,
        id: item.native_id,
        resource_id: item.native_id,
        native_id: item.native_id,
        name: item.name,
        native_name: item.name,
        resource_type: item.resource_type,
        resource_native_type: item.resource_type,
        cloud: item.cloud,
        provider: item.cloud,
        account: item.account_uuid,
        account_id: item.account_native_id,
        account_native_id: item.account_native_id,
        account_uuid: item.account_uuid,
        environment_id: item.environment_id,
        region: item.region,
        arn: item.arn,
        console_url: item.console_url,
        tags: item.tags ?? {},
        contexts: [],
        user_tags: [],
        attrs,
        os_name: attrs.os_name,
        os_version: attrs.os_version,
        state: attrs.state,
        runtime: attrs.runtime,
        image:
          attrs.image ??
          attrs.image_ref ??
          attrs.image_uri ??
          attrs.image_name ??
          attrs.image_id,
        image_id: attrs.image_id,
        image_digest: attrs.image_digest,
        image_tags: attrs.image_tags,
        namespace: attrs.namespace,
        deployed_in: normalizedDeployedIn,
        vpc: attrs.vpc ?? attrs.vpc_name,
        vpc_id: attrs.vpc_id,
        repository_name: attrs.repository_name,
        repository_uri: attrs.repository_uri,
        registry_id: attrs.registry_id,
        vulnerabilities: item.vulnerabilities ?? {},
        applications: item.applications ?? [],
        last_seen: item.vulnerability_last_seen_at || item.last_seen_at,
        status: item.status,
      };
    }),
    total: response.total_count ?? response.items.length,
    page,
    size,
  };
}

/**
 * One page of CVE hosts. Resource insight pages by cursor, so callers fetch
 * through the requested page and this slices it out.
 */
export function mapCveHostsToLegacy(
  response: VulnerabilityResourceInsightResponse,
  page: number,
  size: number,
) {
  const legacy = mapVulnerabilityResourceInsightToLegacy(response, page, size);
  return {
    ...legacy,
    // The hosts table keys icons and per-type summaries by native_name.
    items: legacy.items
      .slice((page - 1) * size, page * size)
      .map(item => ({ ...item, native_name: item.resource_type })),
  };
}

export const dashboardSeverityKeys = severities;
